/**
 * currency.ts — global currency formatting utilities.
 *
 * `useCurrency()` (React hook) populates the cache on mount.
 * `fmtCurrency()` and `fmtMoney()` can be called anywhere — in tables,
 * toast messages, helper functions, and non-hook contexts.
 *
 * All pages should import from here instead of hard-coding "$".
 */

interface CurrencyConfig {
  symbol: string;
  code: string;
  locale: string;
  decimals: number;
}

const DEFAULT: CurrencyConfig = {
  symbol: '$', code: 'USD', locale: 'en-US', decimals: 2,
};

// Global cache — written by useCurrency() on every render, readable everywhere
let _cfg: CurrencyConfig = { ...DEFAULT };

/** Called by useCurrency hook to keep cache current */
export function _setCurrencyConfig(cfg: CurrencyConfig): void {
  _cfg = cfg;
}

/** Read current config (safe outside React) */
export function getCurrencyConfig(): CurrencyConfig { return _cfg; }

/**
 * Format a number as a currency string.
 * Uses the tenant's configured symbol, locale, and decimal precision.
 *
 * fmt(1234.5)   →  "Rs 1,234.50"   or  "$1,234.50"
 */
export function fmtCurrency(
  amount: number | string | null | undefined,
  opts?: { decimals?: number }
): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  if (!isFinite(n)) return `${_cfg.symbol}0.${'0'.repeat(_cfg.decimals)}`;
  const dec = opts?.decimals ?? _cfg.decimals;
  try {
    return new Intl.NumberFormat(_cfg.locale, {
      style: 'currency',
      currency: _cfg.code,
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }).format(n);
  } catch {
    return `${_cfg.symbol}${n.toFixed(dec)}`;
  }
}

/**
 * Compact formatter for large numbers (KPIs, charts).
 * fmt(1_234_500) → "$1.2M"   fmt(12_000) → "$12.0K"
 */
export function fmtMoney(amount: number | string | null | undefined): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  if (!isFinite(n)) return `${_cfg.symbol}0`;
  const sym = _cfg.symbol;
  if (Math.abs(n) >= 1e6) return `${sym}${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${sym}${(n / 1e3).toFixed(1)}K`;
  return `${sym}${n.toFixed(_cfg.decimals)}`;
}

/** Just the symbol — for labels, placeholders */
export function currencySymbol(): string { return _cfg.symbol; }
